/**
 * /admin/settings/homepage — full-page editor for the public landing
 * page. Loads the section list from `/api/homepage-config/admin`,
 * lets admins reorder (up/down arrows), enable/disable, and edit
 * each section's content. Saves back via PUT.
 *
 * Each section type has a dedicated editor component below — they all
 * read/write into the section's `content` blob. Item lists support
 * add/remove/reorder.
 */
import { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Loader2,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  ChevronRight,
  Trash2,
  EyeOff,
  Plus,
  RotateCcw,
  Save,
  Eye,
  Info,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { CookieNoticeCard } from './homepage/CookieNoticeCard';

/* -------------------------------------------------------------------
 * Item-list helper — used by every section that has a `content.items`,
 * `content.feature_cards`, `content.checklist`, etc array. Renders a
 * scrollable list with up/down/delete controls and a "+ Add" button.
 * ------------------------------------------------------------------*/
function ItemList({ items, onChange, fields, addLabel = 'Add item', defaultItem }) {
  const move = (idx, dir) => {
    const next = [...items];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const update = (idx, patch) => onChange(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const add = () => onChange([...items, { ...(defaultItem || {}) }]);

  return (
    <div className="space-y-3">
      {items.map((it, idx) => (
        <div key={idx} className={`border rounded-lg p-3 bg-slate-50 space-y-2 ${it.hidden ? 'opacity-50' : ''}`} data-testid={`item-row-${idx}`}>
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-slate-500">Item {idx + 1}{it.hidden ? ' · hidden' : ''}</span>
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => update(idx, { hidden: !it.hidden })}
                className="h-7 w-7 p-0"
                title={it.hidden ? 'Show on site' : 'Hide from site'}
                data-testid={`item-visibility-${idx}`}
              >
                {it.hidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => move(idx, -1)}
                disabled={idx === 0}
                className="h-7 w-7 p-0"
                data-testid={`item-up-${idx}`}
              >
                <ArrowUp className="h-3.5 w-3.5" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => move(idx, 1)}
                disabled={idx === items.length - 1}
                className="h-7 w-7 p-0"
                data-testid={`item-down-${idx}`}
              >
                <ArrowDown className="h-3.5 w-3.5" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { if (window.confirm('Delete this item permanently? Use the eye icon to just hide it.')) remove(idx); }}
                className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                data-testid={`item-remove-${idx}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {fields.map((f) => (
              <div key={f.key} className={f.full ? 'md:col-span-2' : ''}>
                <Label className="text-xs text-slate-600">{f.label}</Label>
                {f.type === 'textarea' ? (
                  <Textarea
                    rows={2}
                    value={it[f.key] || ''}
                    onChange={(e) => update(idx, { [f.key]: e.target.value })}
                    className="text-sm"
                    data-testid={`item-${idx}-${f.key}`}
                  />
                ) : f.type === 'select' ? (
                  <select
                    value={it[f.key] || f.options[0][0]}
                    onChange={(e) => update(idx, { [f.key]: e.target.value })}
                    className="h-8 w-full rounded-md border border-input bg-white px-2 text-sm"
                    data-testid={`item-${idx}-${f.key}`}
                  >
                    {f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                ) : f.type === 'list' ? (
                  <Textarea
                    rows={3}
                    value={(it[f.key] || []).join('\n')}
                    onChange={(e) =>
                      update(idx, {
                        [f.key]: e.target.value
                          .split('\n')
                          .map((s) => s.trim())
                          .filter(Boolean),
                      })
                    }
                    placeholder="One per line"
                    className="text-sm font-mono"
                    data-testid={`item-${idx}-${f.key}`}
                  />
                ) : (
                  <Input
                    value={it[f.key] || ''}
                    onChange={(e) => update(idx, { [f.key]: e.target.value })}
                    className="h-8 text-sm"
                    data-testid={`item-${idx}-${f.key}`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={add} data-testid="item-add-btn">
        <Plus className="h-3.5 w-3.5 mr-1" />
        {addLabel}
      </Button>
    </div>
  );
}

/* -------------------------------------------------------------------
 * Per-section editors. Each one reads the `content` blob and emits
 * mutations via `onContent`. Keep them dumb — no fetching here.
 * ------------------------------------------------------------------*/
function HeroEditor({ content, onContent }) {
  const set = (patch) => onContent({ ...content, ...patch });
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Tagline pill" value={content.tagline_pill} onChange={(v) => set({ tagline_pill: v })} />
        <Field label="Headline prefix" value={content.headline_prefix} onChange={(v) => set({ headline_prefix: v })} />
        <Field label="Headline highlight" value={content.headline_highlight} onChange={(v) => set({ headline_highlight: v })} />
      </div>
      <Field label="Subheading" type="textarea" value={content.subheading} onChange={(v) => set({ subheading: v })} />
      <div>
        <Label className="text-xs text-slate-600 mb-1 block">Feature cards (4 cards)</Label>
        <ItemList
          items={content.feature_cards || []}
          onChange={(v) => set({ feature_cards: v })}
          defaultItem={{ icon: 'star', title: '', subtitle: '', color: 'blue' }}
          fields={[
            { key: 'icon', label: 'Icon (trophy/users/target/star/map-pin/calendar/repeat)' },
            { key: 'color', label: 'Color (blue/emerald/amber/red/purple/teal/indigo/violet)' },
            { key: 'title', label: 'Title' },
            { key: 'subtitle', label: 'Subtitle' },
          ]}
        />
      </div>
      <HeroPanelEditor content={content} set={set} />
    </div>
  );
}

const HERO_ACTIONS = [['signup', 'Open sign-up'], ['signin', 'Open sign-in'], ['trial', 'Open free-trial form'], ['url', 'Go to a link']];
const DEFAULT_QUICK_LINKS = [
  { label: 'Sign In', action: 'signin' },
  { label: 'Free Trial', action: 'trial' },
  { label: 'Our Players', action: 'url', url: '/our-players' },
];

// Right-hand photo panel + the big button and the small links under it.
function HeroPanelEditor({ content, set }) {
  return (
    <div className="border-t pt-4 space-y-4" data-testid="hero-panel-editor">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">Right-side photo panel</p>
          <p className="text-xs text-slate-500">When hidden, the hero text and button move to the centre. Photos are managed in <a href="/admin/hero-images" className="underline" data-testid="hero-images-link">Hero Images</a>.</p>
        </div>
        <Switch checked={content.show_media_panel !== false} onCheckedChange={(v) => set({ show_media_panel: v })} data-testid="hero-media-panel-toggle" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Big button text" value={content.cta_primary_text} onChange={(v) => set({ cta_primary_text: v })} />
        <div>
          <Label className="text-xs text-slate-600">Big button goes to</Label>
          <select value={content.cta_primary_action || 'signup'} onChange={(e) => set({ cta_primary_action: e.target.value })} className="h-8 w-full rounded-md border border-input bg-white px-2 text-sm" data-testid="hero-cta-action">
            {HERO_ACTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        {content.cta_primary_action === 'url' && (
          <Field label="Button link (/page, #section or https://…)" value={content.cta_primary_url} onChange={(v) => set({ cta_primary_url: v })} />
        )}
      </div>
      <div>
        <Label className="text-xs text-slate-600 mb-1 block">Links under the button</Label>
        <ItemList
          items={Array.isArray(content.quick_links) ? content.quick_links : DEFAULT_QUICK_LINKS}
          onChange={(v) => set({ quick_links: v })}
          defaultItem={{ label: '', action: 'url', url: '' }}
          addLabel="Add link"
          fields={[
            { key: 'label', label: 'Text' },
            { key: 'action', label: 'Goes to', type: 'select', options: HERO_ACTIONS },
            { key: 'url', label: 'Link (only for "Go to a link")', full: true },
          ]}
        />
      </div>
    </div>
  );
}

function TextEditor({ content, onContent }) {
  return (
    <div className="space-y-3">
      <Field label="Title" value={content.title} onChange={(v) => onContent({ ...content, title: v })} />
      <Field label="Body" type="textarea" value={content.body} onChange={(v) => onContent({ ...content, body: v })} />
    </div>
  );
}

function IconGridEditor({ content, onContent }) {
  const itemFields = (content.items || []).some((i) => 'emoji' in i)
    ? [
        { key: 'emoji', label: 'Emoji' },
        { key: 'title', label: 'Title' },
        { key: 'desc', label: 'Description', full: true },
      ]
    : [
        { key: 'icon', label: 'Icon (users/target/trophy/star)' },
        { key: 'color', label: 'Color' },
        { key: 'title', label: 'Title' },
        { key: 'desc', label: 'Description', full: true },
      ];
  return (
    <div className="space-y-3">
      <Field label="Title" value={content.title} onChange={(v) => onContent({ ...content, title: v })} />
      <Field label="Subtitle" value={content.subtitle} onChange={(v) => onContent({ ...content, subtitle: v })} />
      <Label className="text-xs text-slate-600">Items</Label>
      <ItemList
        items={content.items || []}
        onChange={(v) => onContent({ ...content, items: v })}
        defaultItem={{ icon: 'star', title: '', desc: '', color: 'blue' }}
        fields={itemFields}
      />
    </div>
  );
}

function FeatureSplitEditor({ content, onContent }) {
  const set = (patch) => onContent({ ...content, ...patch });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Title prefix" value={content.title_prefix} onChange={(v) => set({ title_prefix: v })} />
        <Field label="Title highlight" value={content.title_highlight} onChange={(v) => set({ title_highlight: v })} />
      </div>
      <Field label="Body" type="textarea" value={content.body} onChange={(v) => set({ body: v })} />
      <Label className="text-xs text-slate-600">Checklist items</Label>
      <ItemList
        items={content.checklist || []}
        onChange={(v) => set({ checklist: v })}
        defaultItem={{ title: '', desc: '' }}
        fields={[
          { key: 'title', label: 'Title' },
          { key: 'desc', label: 'Description', full: true },
        ]}
      />
      <Label className="text-xs text-slate-600 pt-2">Cards (right column)</Label>
      <ItemList
        items={content.cards || []}
        onChange={(v) => set({ cards: v })}
        defaultItem={{ icon: 'trophy', title: '', desc: '', color: 'blue' }}
        fields={[
          { key: 'icon', label: 'Icon' },
          { key: 'color', label: 'Color' },
          { key: 'title', label: 'Title' },
          { key: 'desc', label: 'Description', full: true },
        ]}
      />
    </div>
  );
}

function PathwayEditor({ content, onContent }) {
  const set = (patch) => onContent({ ...content, ...patch });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Title prefix" value={content.title_prefix} onChange={(v) => set({ title_prefix: v })} />
        <Field label="Title highlight" value={content.title_highlight} onChange={(v) => set({ title_highlight: v })} />
      </div>
      <Field label="Subtitle" type="textarea" value={content.subtitle} onChange={(v) => set({ subtitle: v })} />
      <Label className="text-xs text-slate-600">Player levels (timeline)</Label>
      <ItemList
        items={content.levels || []}
        onChange={(v) => set({ levels: v })}
        defaultItem={{ level: '', age: '', focus: '', color: 'blue', skills: [] }}
        fields={[
          { key: 'level', label: 'Level name' },
          { key: 'age', label: 'Age range' },
          { key: 'focus', label: 'Focus' },
          { key: 'color', label: 'Color' },
          { key: 'skills', label: 'Skills (one per line)', type: 'list', full: true },
        ]}
      />
      <Field label="Enrollment section title" value={content.enrollment_title} onChange={(v) => set({ enrollment_title: v })} />
      <Label className="text-xs text-slate-600">Enrollment options</Label>
      <ItemList
        items={content.enrollment_options || []}
        onChange={(v) => set({ enrollment_options: v })}
        defaultItem={{ title: '', desc: '', icon: 'calendar', color: 'blue', features: [], popular: false }}
        fields={[
          { key: 'title', label: 'Title' },
          { key: 'desc', label: 'Description' },
          { key: 'icon', label: 'Icon (calendar/repeat/star)' },
          { key: 'color', label: 'Color' },
          { key: 'features', label: 'Features (one per line)', type: 'list', full: true },
        ]}
      />
    </div>
  );
}

function PeopleGridEditor({ content, onContent }) {
  const set = (patch) => onContent({ ...content, ...patch });
  return (
    <div className="space-y-3">
      <Field label="Title" value={content.title} onChange={(v) => set({ title: v })} />
      <Field label="Subtitle" type="textarea" value={content.subtitle} onChange={(v) => set({ subtitle: v })} />
      <Label className="text-xs text-slate-600">People</Label>
      <ItemList
        items={content.people || []}
        onChange={(v) => set({ people: v })}
        defaultItem={{ name: '', role: '', desc: '', highlight: '' }}
        fields={[
          { key: 'name', label: 'Name' },
          { key: 'role', label: 'Role' },
          { key: 'desc', label: 'Description' },
          { key: 'highlight', label: 'Highlight' },
        ]}
      />
      <Field label="Footer note" value={content.footer_note} onChange={(v) => set({ footer_note: v })} />
    </div>
  );
}

function CtaEditor({ content, onContent }) {
  const set = (patch) => onContent({ ...content, ...patch });
  return (
    <div className="space-y-3">
      <Field label="Title" value={content.title} onChange={(v) => set({ title: v })} />
      <Field label="Body" type="textarea" value={content.body} onChange={(v) => set({ body: v })} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Primary button text" value={content.button_primary_text} onChange={(v) => set({ button_primary_text: v })} />
        <Field
          label="Primary action (trial/signup/url)"
          value={content.button_primary_action}
          onChange={(v) => set({ button_primary_action: v })}
        />
        <Field label="Secondary button text" value={content.button_secondary_text} onChange={(v) => set({ button_secondary_text: v })} />
        <Field
          label="Secondary action (whatsapp/url)"
          value={content.button_secondary_action}
          onChange={(v) => set({ button_secondary_action: v })}
        />
      </div>
    </div>
  );
}

function FooterEditor({ content, onContent }) {
  const set = (patch) => onContent({ ...content, ...patch });
  return (
    <div className="space-y-3">
      <Field label="Copyright text" value={content.copyright_text} onChange={(v) => set({ copyright_text: v })} />
      <ToggleField
        label="Show social links"
        checked={content.show_social}
        onChange={(v) => set({ show_social: v })}
      />
      <ToggleField
        label="Show legal links (Privacy / Terms / etc.)"
        checked={content.show_legal_links}
        onChange={(v) => set({ show_legal_links: v })}
      />
    </div>
  );
}

function HeaderEditor({ content, onContent }) {
  const set = (patch) => onContent({ ...content, ...patch });
  return (
    <div className="space-y-3">
      <ToggleField
        label="Show academy tagline"
        checked={content.show_tagline}
        onChange={(v) => set({ show_tagline: v })}
      />
      <ToggleField
        label="Show address"
        checked={content.show_address}
        onChange={(v) => set({ show_address: v })}
      />
      <div className="pt-2">
        <p className="text-sm font-medium text-slate-800">Top menu bar</p>
        <p className="text-xs text-slate-500 mb-2">Link: <code>#programs</code> scrolls to a section on this page; <code>/book-lane</code> or a full URL opens a page. Use the eye icon to hide an item.</p>
        <ItemList
          items={content.menu_items || []}
          onChange={(v) => set({ menu_items: v })}
          fields={[{ key: 'label', label: 'Label' }, { key: 'link', label: 'Link' }]}
          addLabel="Add menu item"
          defaultItem={{ label: 'New link', link: '/' }}
        />
      </div>
    </div>
  );
}

const SECTION_EDITORS = {
  hero: HeroEditor,
  text: TextEditor,
  icon_grid: IconGridEditor,
  feature_split: FeatureSplitEditor,
  pathway: PathwayEditor,
  people_grid: PeopleGridEditor,
  cta: CtaEditor,
  footer: FooterEditor,
  header: HeaderEditor,
};

/* -------------------------------------------------------------------
 * Tiny UI helpers
 * ------------------------------------------------------------------*/
function Field({ label, value, onChange, type = 'text' }) {
  return (
    <div>
      <Label className="text-xs text-slate-600">{label}</Label>
      {type === 'textarea' ? (
        <Textarea rows={3} value={value || ''} onChange={(e) => onChange(e.target.value)} className="text-sm" />
      ) : (
        <Input value={value || ''} onChange={(e) => onChange(e.target.value)} className="h-8 text-sm" />
      )}
    </div>
  );
}

function ToggleField({ label, checked, onChange }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm">{label}</span>
      <Switch checked={!!checked} onCheckedChange={onChange} />
    </div>
  );
}

/* -------------------------------------------------------------------
 * Page
 * ------------------------------------------------------------------*/
export default function HomepageEditor() {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/homepage-config/admin');
      setSections((data.sections || []).sort((a, b) => (a.order ?? 99) - (b.order ?? 99)));
    } catch {
      toast.error('Failed to load homepage config');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/homepage-config/admin', { sections });
      toast.success('Homepage saved. Refresh the public page to see changes.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm('Reset homepage to factory defaults? This will overwrite any edits.')) return;
    try {
      const { data } = await api.post('/homepage-config/admin/reset-defaults');
      setSections((data.sections || []).sort((a, b) => (a.order ?? 99) - (b.order ?? 99)));
      toast.success('Homepage reset to defaults.');
    } catch {
      toast.error('Reset failed');
    }
  };

  const moveSection = (idx, dir) => {
    const next = [...sections];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setSections(next.map((s, i) => ({ ...s, order: i })));
  };

  const updateSection = (idx, patch) => {
    setSections(sections.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        <div className="space-y-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Homepage Editor</h1>
            <p className="text-sm text-slate-500">
              Reorder, enable/disable, and edit every section of the public landing page.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => window.open('/', '_blank')} data-testid="preview-public-page">
              <Eye className="h-4 w-4 mr-1" /> Preview
            </Button>
            <Button variant="outline" size="sm" onClick={reset} data-testid="reset-defaults-btn">
              <RotateCcw className="h-4 w-4 mr-1" /> Reset
            </Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-homepage-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
              Save
            </Button>
          </div>
        </div>

        {/* iter316 — Help banner: this page configures section LAYOUT/visibility.
            The actual text values (academy name, address, tagline, business hours)
            live in Business Settings. Many users get lost looking for them here. */}
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900 flex items-start gap-2" data-testid="homepage-editor-hint">
          <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold mb-1">Looking for academy name, address, business hours, tagline?</p>
            <p>
              This page controls which sections appear and their <em>section-specific</em> text (hero headline, mission copy, programs list). Your <strong>business name, address, phone, tagline, and weekly hours</strong> live in{" "}
              <a href="/admin/business-hours" className="underline font-medium">Business Settings → Contact Info / Hours</a>{" "}— editing them there updates the landing page header, footer, and any &ldquo;show address/tagline&rdquo; toggles below.
            </p>
          </div>
        </div>

        {sections.map((s, idx) => {
          const Editor = SECTION_EDITORS[s.type] || (() => <div className="text-slate-400 text-sm">No editor for type {s.type}</div>);
          const open = openId === s.id;
          return (
            <Card key={s.id} className={`${s.enabled ? '' : 'opacity-60'}`} data-testid={`section-card-${s.id}`}>
              <CardHeader className="py-3">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setOpenId(open ? null : s.id)}
                    className="text-slate-500 hover:text-slate-900"
                    data-testid={`section-toggle-${s.id}`}
                  >
                    {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                  <CardTitle className="text-base flex-1">
                    {s.label || s.id}
                    <span className="text-xs text-slate-400 ml-2">({s.type})</span>
                  </CardTitle>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => updateSection(idx, { enabled: !s.enabled })}
                    className="h-7 px-2 gap-1 text-xs"
                    title={s.enabled ? 'Visible — click to hide' : 'Hidden — click to show'}
                    data-testid={`section-enabled-${s.id}`}
                  >
                    {s.enabled ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4 text-slate-400" />}
                    {s.enabled ? 'Visible' : 'Hidden'}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => moveSection(idx, -1)}
                    disabled={idx === 0}
                    className="h-7 w-7 p-0"
                    data-testid={`section-up-${s.id}`}
                  >
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => moveSection(idx, 1)}
                    disabled={idx === sections.length - 1}
                    className="h-7 w-7 p-0"
                    data-testid={`section-down-${s.id}`}
                  >
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              {open && (
                <CardContent className="border-t pt-4">
                  <Editor
                    content={s.content || {}}
                    onContent={(c) => updateSection(idx, { content: c })}
                  />
                </CardContent>
              )}
            </Card>
          );
        })}

        <CookieNoticeCard />

        {/* Sticky save bar so admins editing the bottom sections don't lose work */}
        <div className="sticky bottom-4 flex justify-end">
          <Button onClick={save} disabled={saving} size="lg" className="bg-emerald-600 hover:bg-emerald-700 shadow-lg" data-testid="save-homepage-btn-sticky">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            Save changes
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
